import { createContext, useState, useMemo } from 'react';
import { createTheme } from '@mui/material/styles';

// Design tokens generator
export const tokens = (mode) => {
  const grey = {
    100: '#e0e0e0', 200: '#c2c2c2', 300: '#a3a3a3', 400: '#858585', 500: '#666666',
    600: '#525252', 700: '#3d3d3d', 800: '#292929', 900: '#141414'
  };
  const blueAccent = {
    100: '#E8F4FF', 200: '#C9E7FF', 300: '#94D0FF', 400: '#5DB4EE', 500: '#2D93CF',
    600: '#1E6E9D', 700: '#164E70', 800: '#0F354C', 900: '#082233'
  };
  const alamoRed = {
    100: '#F9E4E0',
    200: '#F0BEB4',
    300: '#E08F7E',
    400: '#C95B42',
    500: '#A62305',
    600: '#9B1D00',
    700: '#841C00',
    800: '#661500',
    900: '#3F0E00'
  };
  const alamoOrange = {
    100: '#FFF4E6',
    200: '#FFE3BD',
    300: '#FFD08F',
    400: '#FDBA6C',
    500: '#F89B2E',
    600: '#D98219',
    700: '#A95E0C',
    800: '#743F08',
    900: '#4A2703'
  };
  const greenAccent = {
    100: '#d1fae5', 200: '#a7f3d0', 300: '#6ee7b7', 400: '#34d399', 500: '#10B981',
    600: '#059669', 700: '#047857', 800: '#065f46', 900: '#064e3b'
  };
  const redAccent = {
    100: '#fee2e2', 200: '#fecaca', 300: '#fca5a5', 400: '#f87171', 500: '#EF4444',
    600: '#dc2626', 700: '#b91c1c', 800: '#991b1b', 900: '#7f1d1d'
  };
  return {
    grey,
    primary: mode === 'light' ? alamoRed : grey,
    secondary: mode === 'light' ? alamoOrange : greenAccent,
    blueAccent,
    alamoRed,
    alamoOrange,
    greenAccent,
    redAccent
  };
};

// MUI theme settings using our tokens
export const themeSettings = (mode) => {
  const colors = tokens(mode);

  // Define an Alamo Center brand gradient for the sidebar
  const sidebarGradient =
    'linear-gradient(180deg, ' + colors.alamoRed[700] + ' 0%, ' + colors.alamoRed[600] + ' 52%, ' + colors.alamoOrange[600] + ' 100%)';

  // Alamo Center brand palette
  const primaryPalette = {
    light: colors.alamoRed[300],
    main:  colors.alamoRed[600],
    dark:  colors.alamoRed[800]
  };
  const secondaryPalette = {
    light: colors.secondary[300],
    main:  colors.secondary[500],
    dark:  colors.secondary[700]
  };

  return createTheme({
    palette: {
      mode,
      primary:   primaryPalette,
      secondary: secondaryPalette,
      background: {
        default: mode === 'dark' ? colors.grey[900] : '#FFFFFF',
        paper:   mode === 'dark' ? colors.grey[800] : '#F6F6F6'
      },
      error:   { main: colors.redAccent[500] },
      warning: { main: colors.redAccent[400] },
      info:    { main: colors.blueAccent[500] },
      success: { main: colors.greenAccent[500] }
    },
    typography: {
      fontFamily: ['Inter', 'sans-serif'].join(','),
      fontSize:   14,
      h1:    { fontSize: '2.5rem', fontWeight: 700 },
      h2:    { fontSize: '2rem',   fontWeight: 600 },
      h3:    { fontSize: '1.75rem',fontWeight: 500 },
      body1: { fontSize: '1rem' },
      body2: { fontSize: '0.875rem' }
    },
    shape: { borderRadius: 8 },
    components: {
      MuiDrawer: {
        styleOverrides: {
          paper: {
            background: mode === 'light'
              ? sidebarGradient
              : colors.grey[900]
          }
        }
      }
    }
  });
};

// Color mode context & hook
export const colorModeContext = createContext({ toggleColorMode: () => {} });
export const useMode = () => {
  const [mode, setMode] = useState('light');
  const colorMode = useMemo(
    () => ({ toggleColorMode: () => setMode(prev => (prev === 'light' ? 'dark' : 'light')) }),
    []
  );
  const theme = useMemo(() => themeSettings(mode), [mode]);
  return [theme, colorMode];
};
